import { Modal, Select, Spin, Empty, Button, message } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectInvestors,
  selectInvestorsLoadingState,
} from '@/store/features/investors/investorsSlice';
import { setSelectedInvestor } from '@/store/features/planning/planningSlice';
import {
  getSessionAssociationByThreadId,
  createSessionAssociation,
  updateSessionAssociationRecord,
  deleteSessionAssociationRecord,
} from '@/api/weave-x';
import { getThreadState } from '@/api/weave-x-agents';
import { extractThreadNameFromState } from '@/common/components/wavvest-planning/orchestrator-message-adapter';
import { getInvestorDisplayName, getInvestorOptionLabel } from '@/util/investor-display';
import './assign-modal.scss';

interface AssignModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AssignmentInfo = {
  assignmentId: string;
  investorId: string;
  investorName?: string;
} | null;

export function AssignModal({ isOpen, onClose }: AssignModalProps) {
  const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(null);
  const [assignmentInfo, setAssignmentInfo] = useState<AssignmentInfo>(null);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const investors = useAppSelector(selectInvestors);
  const investorsLoading = useAppSelector(selectInvestorsLoadingState) === 'pending';
  const { currentThreadId } = useAppSelector((state) => state.canvas);
  const dispatch = useAppDispatch();

  const investorMap = useMemo(() => {
    const map = new Map<string, string>();
    investors.forEach((investor) => {
      map.set(investor.id, getInvestorDisplayName(investor));
    });
    return map;
  }, [investors]);

  // Fetch current assignment status when modal opens
  useEffect(() => {
    if (isOpen && currentThreadId) {
      setIsLoadingAssignment(true);
      getSessionAssociationByThreadId(currentThreadId)
        .then((response) => {
          if (response) {
            setAssignmentInfo({
              assignmentId: response.assignmentId,
              investorId: response.investorId,
              investorName: investorMap.get(response.investorId),
            });
            setSelectedInvestorId(response.investorId);
          } else {
            setAssignmentInfo(null);
            setSelectedInvestorId(null);
          }
        })
        .catch((error) => {
          console.error('Error fetching assignment:', error);
          setAssignmentInfo(null);
          setSelectedInvestorId(null);
        })
        .finally(() => {
          setIsLoadingAssignment(false);
        });
    }
  }, [isOpen, currentThreadId, investorMap]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAssignmentInfo(null);
      setSelectedInvestorId(null);
    }
  }, [isOpen]);

  const handleInvestorSelect = (investorId: string) => {
    setSelectedInvestorId(investorId);
  };

  const handleAssign = async () => {
    if (!selectedInvestorId || !currentThreadId) {
      return;
    }

    try {
      setIsAssigning(true);

      // Get thread information to extract the title
      const thread = await getThreadState(currentThreadId);
      const threadTitle = extractThreadNameFromState(thread, 'Untitled Plan');
      const selectedInvestor = investors.find((investor) => investor.id === selectedInvestorId);

      const assignmentPayload = {
        investorId: selectedInvestorId,
        cognitoId: selectedInvestor?.cognito_id ?? undefined,
        threadId: currentThreadId,
        title: threadTitle,
        description: '',
        notes: '',
      };

      if (assignmentInfo?.assignmentId && assignmentInfo.investorId === selectedInvestorId) {
        await updateSessionAssociationRecord(assignmentInfo.assignmentId, {
          title: threadTitle,
          description: '',
          notes: '',
          cognitoId: selectedInvestor?.cognito_id ?? undefined,
        });
      } else if (assignmentInfo?.assignmentId) {
        const replacementAssignment = await createSessionAssociation(assignmentPayload);

        try {
          await deleteSessionAssociationRecord(assignmentInfo.assignmentId);
        } catch (deleteError) {
          try {
            await deleteSessionAssociationRecord(replacementAssignment.assignmentId);
          } catch (rollbackError) {
            console.error('Failed to roll back replacement assignment:', rollbackError);
          }

          throw deleteError;
        }
      } else {
        await createSessionAssociation(assignmentPayload);
      }

      message.success(
        assignmentInfo ? 'Session reassigned successfully' : 'Session assigned successfully',
      );
      onClose();

      try {
        const updatedAssignment = await getSessionAssociationByThreadId(currentThreadId);
        if (updatedAssignment) {
          setAssignmentInfo({
            assignmentId: updatedAssignment.assignmentId,
            investorId: updatedAssignment.investorId,
            investorName: investorMap.get(updatedAssignment.investorId),
          });
          setSelectedInvestorId(updatedAssignment.investorId);

          // Sync Redux state so the header updates
          const investor = investors.find(
            (candidate) => candidate.id === updatedAssignment.investorId,
          );
          dispatch(
            setSelectedInvestor({
              id: updatedAssignment.investorId,
              cognitoId: investor?.cognito_id ?? undefined,
              name: investor ? getInvestorDisplayName(investor) : undefined,
            }),
          );
        }
      } catch (refreshError) {
        console.error('Assignment saved, but refresh failed:', refreshError);
      }
    } catch (error) {
      console.error('Error assigning session:', error);
      message.error('Failed to assign session. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  const renderSubtitle = () => {
    if (isLoadingAssignment) {
      return null;
    }

    if (assignmentInfo) {
      const investorName =
        assignmentInfo.investorName || investorMap.get(assignmentInfo.investorId) || 'Unknown';
      return (
        <p className="assign-modal-subtitle">
          Currently assigned to <strong>{investorName}</strong>. Choose another investor to change
          it.
        </p>
      );
    }

    return (
      <p className="assign-modal-subtitle">
        This plan isn't assigned yet — choose an investor to continue.
      </p>
    );
  };

  const renderContent = () => {
    if (!currentThreadId) {
      return (
        <div className="assign-modal-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="assign-modal-empty-text">
                No active session yet. Start a conversation to create a session that can be assigned
                to an investor.
              </span>
            }
          />
        </div>
      );
    }

    if (isLoadingAssignment) {
      return (
        <div className="assign-modal-loading">
          <Spin />
        </div>
      );
    }

    return (
      <>
        {renderSubtitle()}
        <div className="assign-modal-form-section">
          <label className="assign-modal-label">Select Investor</label>
          <Select
            placeholder="Select an investor"
            className="assign-modal-select"
            classNames={{ popup: { root: 'assign-modal-select-dropdown' } }}
            loading={investorsLoading}
            onChange={handleInvestorSelect}
            value={selectedInvestorId}
            optionFilterProp="label"
            showSearch
            size="large"
          >
            {investors.map((investor) => {
              const displayName = investorMap.get(investor.id) ?? getInvestorDisplayName(investor);

              return (
                <Select.Option
                  key={investor.id}
                  value={investor.id}
                  label={getInvestorOptionLabel(investor)}
                >
                  <div className="assign-modal-investor-option">
                    <span className="investor-name">{displayName}</span>
                    {investor.email && <span className="investor-email">{investor.email}</span>}
                  </div>
                </Select.Option>
              );
            })}
          </Select>
        </div>
        <div className="assign-modal-button-section">
          <Button
            type="primary"
            onClick={handleAssign}
            loading={isAssigning}
            disabled={!selectedInvestorId}
            className="assign-modal-button"
          >
            Assign
          </Button>
        </div>
      </>
    );
  };

  return (
    <Modal
      title="Assign to an Investor"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      className="assign-modal"
      centered
    >
      <div className="assign-modal-content">{renderContent()}</div>
    </Modal>
  );
}
